import {
  ArrowLeft,
  ArrowLeftRight,
  ArrowRight,
  BadgeCheck,
  Banknote,
  Building2,
  Clock3,
  IndianRupee,
  LockKeyhole,
  Quote,
  ShieldCheck,
  Sparkles,
  Users,
  Wand2,
  Zap,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { SiteFrame } from "@/components/AppShell";
import { AssetGlyph, ChangePill, PriceCell, Sparkline, fmtCompact } from "@/components/market/PriceCell";
import { useSparklines } from "@/components/market/useSparklines";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLiveMarkets } from "@/lib/useLiveMarkets";
import { BRAND_LOGO_SRC } from "@/lib/brand";

const ESCROW_IMG = "/assets/paisacx/feature-escrow-v2.jpg";
const OTC_IMG = "/assets/paisacx/feature-otc-v2.jpg";

const Accent = ({ children }) => <span className="brand-text">{children}</span>;

function SectionTitle({ children, action }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 sm:gap-4">
      <h2 className="max-w-2xl font-display text-2xl font-semibold leading-tight sm:text-4xl lg:text-5xl">{children}</h2>
      {action}
    </div>
  );
}

function MoreButton({ to, label }) {
  return (
    <Button asChild variant="outline" className="h-11 border-primary/40 px-5">
      <Link to={to}>
        {label} <ArrowRight />
      </Link>
    </Button>
  );
}

/** Live USDT/INR reference rate shared by the landing sections. */
function useUsdtInr() {
  const { usdtInr, rows } = useLiveMarkets("USDT");
  const row = rows.find((r) => r.symbol === "USDT");
  return { rate: Number(usdtInr) || null, change: row ? Number(row.change_pct_24h) || 0 : null };
}

function Hero() {
  const { user } = useAuth();
  return (
    <section className="relative isolate flex min-h-[min(70vh,640px)] items-center border-b border-border bg-[oklch(0.12_0.02_160)]" data-testid="hero-section">
      <div className="pointer-events-none absolute inset-0 z-0 overflow-hidden" aria-hidden>
        <video
          className="hero-video fixed inset-0 z-0 h-full w-full object-cover object-[92%_40%] sm:object-[80%_70%] lg:object-[72%_100%]"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
        >
          <source src="/hero-bg-v2.mp4" type="video/mp4" />
        </video>

        <div className="absolute inset-0 bg-brand-emerald opacity-40 mix-blend-color sm:opacity-45" />
        <div className="absolute inset-0 hidden bg-[radial-gradient(ellipse_at_75%_55%,oklch(0.74_0.18_153_/_0.28),transparent_55%)] mix-blend-screen sm:block" />
        <div className="hero-grid absolute inset-0 opacity-[0.12]" />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,oklch(0.1_0.025_160_/_0.94)_0%,oklch(0.1_0.025_160_/_0.88)_42%,oklch(0.1_0.025_160_/_0.45)_68%,oklch(0.1_0.025_160_/_0.12)_100%)] sm:bg-[linear-gradient(105deg,oklch(0.12_0.03_160_/_0.84)_0%,oklch(0.12_0.03_160_/_0.42)_48%,oklch(0.12_0.03_160_/_0.1)_100%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(to_top,oklch(0.1_0.025_160_/_0.55)_0%,transparent_45%)] sm:bg-[linear-gradient(to_top,oklch(0.12_0.03_160_/_0.45)_0%,oklch(0.12_0.03_160_/_0.1)_38%,transparent_70%)]" />

        <img
          src={BRAND_LOGO_SRC}
          alt=""
          width={512}
          height={512}
          className="absolute right-8 top-[18%] hidden w-[min(34vw,340px)] opacity-[0.14] lg:right-12 lg:block xl:right-16"
        />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
        <div className="hero-copy relative w-full max-w-xl border-l-2 border-primary pl-4 sm:max-w-2xl sm:pl-7 lg:max-w-3xl" data-testid="hero-copy">
          <div className="flex items-center gap-2.5 sm:gap-4">
            <img src={BRAND_LOGO_SRC} alt="" width={56} height={56} className="h-9 w-9 object-contain sm:h-12 sm:w-12 lg:h-14 lg:w-14" />
            <p className="font-display text-4xl font-bold leading-none tracking-tight text-white sm:text-6xl lg:text-7xl">
              Paisa<span className="brand-text">CX</span>
            </p>
          </div>

          <h1 className="mt-5 max-w-2xl text-xl font-semibold leading-snug text-white sm:mt-7 sm:text-4xl lg:text-[2.75rem] lg:leading-[1.15]">
            Trade Crypto with <span className="brand-text">Confidence!</span>
          </h1>

          <p className="mt-3 max-w-md text-sm leading-6 text-white/75 sm:mt-4 sm:text-base sm:leading-7">
            Secure INR on-ramp and off-ramp — escrow-backed P2P, instant UPI rails and a dedicated OTC desk.
          </p>

          <div className="mt-6 flex flex-col gap-3 sm:mt-8 sm:flex-row">
            <Button asChild size="lg" className="brand-gradient relative z-10 h-12 w-full px-7 shadow-brand sm:w-auto">
              <Link to={user ? "/p2p" : "/register"} data-testid="hero-start-trading">
                Start trading now <ArrowRight />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="relative z-10 h-12 w-full border-white/25 bg-[oklch(0.12_0.03_160_/_0.55)] px-7 text-white backdrop-blur-sm hover:bg-white/15 hover:text-white sm:w-auto sm:bg-white/10"
            >
              <Link to="/p2p" data-testid="hero-view-exchange">
                Browse P2P offers
              </Link>
            </Button>
          </div>

          <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-white/60 sm:mt-7 sm:text-sm">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 shrink-0 text-primary" /> Bank-level security
            </span>
            <span className="inline-flex items-center gap-2">
              <Zap className="h-4 w-4 shrink-0 text-primary" /> Instant settlements
            </span>
            <span className="inline-flex items-center gap-2">
              <Clock3 className="h-4 w-4 shrink-0 text-primary" /> 24/7 support
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}

function timeAgo(iso) {
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min${s < 120 ? "" : "s"} ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr${s < 7200 ? "" : "s"} ago`;
  return `${Math.floor(s / 86400)} day${s < 172800 ? "" : "s"} ago`;
}

function useBestP2PRates(enabled) {
  const [best, setBest] = useState({ buy: null, sell: null });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const top = (side) =>
      api
        .get("/api/p2p/ads", { params: { side, asset: "USDT", fiat: "INR", limit: 1 } })
        .then(({ data }) => Number(data?.ads?.[0]?.price) || null)
        .catch(() => null);
    const pull = () => Promise.all([top("buy"), top("sell")]).then(([buy, sell]) => alive && setBest({ buy, sell }));
    pull();
    const id = setInterval(pull, 15000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [enabled]);
  return best;
}

function LiveInsights({ fx }) {
  const { user } = useAuth();
  const best = useBestP2PRates(Boolean(user));
  const sparkOf = useSparklines(["USDT"], "INR");
  const { recent, p2pStats } = useLiveMarkets("USDT");
  const rate = fx.rate;
  const buyRate = best.buy ?? p2pStats?.best_buy ?? null;
  const sellRate = best.sell ?? p2pStats?.best_sell ?? null;
  const releaseMin = p2pStats?.avg_release_min;
  const successRate = p2pStats?.success_rate;
  const speedPct = releaseMin == null ? 0 : Math.max(8, Math.min(100, 100 - (releaseMin / 30) * 100));
  const spark = sparkOf("USDT");

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:py-20" data-testid="crypto-updates">
      <SectionTitle action={<MoreButton to="/markets" label="View markets" />}>
        Live <Accent>market insights</Accent>
      </SectionTitle>
      <p className="mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">
        Real-time USDT/INR rates and settlement metrics for Indian traders.
      </p>

      <div className="mt-8 grid items-stretch gap-4 sm:mt-10 lg:grid-cols-3">
        <div className="interactive-tile panel-gradient flex h-full flex-col rounded-xl border border-border p-5 sm:p-6">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-muted-foreground">USDT / INR rate</p>
            {fx.change != null && <ChangePill value={fx.change} />}
          </div>
          {rate ? (
            <p className="num mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">₹{rate.toFixed(2)}</p>
          ) : (
            <div className="skeleton mt-3 h-10 w-40" />
          )}
          <p className="mt-2 text-xs text-muted-foreground">24h movement · competitive P2P spreads</p>
          {spark?.length > 1 ? (
            <div className="mt-5">
              <Sparkline data={spark} width={220} height={40} up={fx.change >= 0} />
            </div>
          ) : null}
          <div className="mt-auto grid grid-cols-2 gap-3 border-t border-border/80 pt-4">
            <div>
              <p className="text-[11px] text-muted-foreground">Best buy</p>
              <p className="num mt-1 text-sm font-semibold text-up">{buyRate ? `₹${buyRate.toFixed(2)}` : "—"}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Best sell</p>
              <p className="num mt-1 text-sm font-semibold text-down">{sellRate ? `₹${sellRate.toFixed(2)}` : "—"}</p>
            </div>
          </div>
        </div>

        <div className="interactive-tile panel-gradient flex h-full flex-col rounded-xl border border-border p-5 sm:p-6">
          <p className="text-sm font-medium text-muted-foreground">Transaction speed</p>
          <p className="num mt-3 text-3xl font-semibold tracking-tight sm:text-4xl" data-testid="p2p-release-time">
            {releaseMin == null ? "—" : releaseMin}
            <span className="ml-1 text-lg font-medium text-muted-foreground">min</span>
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Average time from order to escrow release, last 30 days
            {p2pStats?.completed_30d ? ` · ${p2pStats.completed_30d} orders` : ""}
          </p>
          <div className="mt-5">
            <div className="flex items-center justify-between text-[11px] text-muted-foreground">
              <span>Escrow release</span>
              <span className="num text-foreground">
                {releaseMin == null ? "No data yet" : releaseMin <= 10 ? "Fast" : releaseMin <= 30 ? "Normal" : "Slow"}
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-background/50">
              <div className="brand-gradient h-full rounded-full transition-all" style={{ width: `${speedPct}%` }} />
            </div>
          </div>
          <div className="mt-auto grid grid-cols-2 gap-3 border-t border-border/80 pt-4">
            <div>
              <p className="text-[11px] text-muted-foreground">Success rate</p>
              <p className="num mt-1 text-sm font-semibold">{successRate == null ? "—" : `${successRate}%`}</p>
            </div>
            <div>
              <p className="text-[11px] text-muted-foreground">Payment rails</p>
              <p className="mt-1 text-sm font-semibold">UPI · IMPS</p>
            </div>
          </div>
        </div>

        <div className="interactive-tile panel-gradient flex h-full flex-col rounded-xl border border-border p-5 sm:p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium text-muted-foreground">Recent transactions</p>
            <span className="inline-flex items-center gap-1.5 text-xs text-up">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-up" /> Live
            </span>
          </div>
          <ul className="mt-4 flex flex-1 flex-col justify-between gap-1">
            {recent.length === 0 ? (
              <li className="flex flex-1 items-center justify-center py-8 text-center text-sm text-muted-foreground">
                No settled trades yet. Completed P2P and OTC trades appear here.
              </li>
            ) : recent.slice(0, 4).map((tx, i) => (
              <li key={`${tx.kind}-${tx.at}-${i}`} className="interactive-row -mx-2 flex items-center justify-between gap-3 rounded-lg px-2 py-1.5 text-sm">
                <div>
                  <p className="num font-semibold">₹{Math.round(tx.inr).toLocaleString("en-IN")}</p>
                  <p className="num text-xs text-muted-foreground">
                    {(tx.qty || (tx.asset === "USDT" && rate ? tx.inr / rate : 0)).toLocaleString("en-IN", { maximumFractionDigits: tx.asset === "BTC" ? 6 : 2 })} {tx.asset}
                    <span className="ml-1.5 uppercase">· {tx.kind}</span>
                  </p>
                </div>
                <div className="text-right">
                  <span className="rounded border border-up/30 bg-up/10 px-1.5 py-0.5 text-[10px] font-semibold text-up">Complete</span>
                  <p className="mt-1 text-[11px] text-muted-foreground">{timeAgo(tx.at)}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

const WHY_CHOOSE = [
  { icon: ShieldCheck, title: "Enterprise security", copy: "Bank-level controls with 2FA, encrypted sessions and escrow custody on every P2P order." },
  { icon: Clock3, title: "24/7 trading", copy: "Buy and sell crypto anytime with instant matching and continuous order execution." },
  { icon: Zap, title: "Lightning settlements", copy: "Fast INR deposits and withdrawals straight to your bank via UPI, IMPS or NEFT." },
  { icon: IndianRupee, title: "Best market rates", copy: "Competitive USDT/INR spreads, transparent fees and live P2P order books." },
  { icon: ArrowLeftRight, title: "Seamless conversions", copy: "Effortless crypto ↔ INR flows with minimal slippage and clear settlement status." },
  { icon: BadgeCheck, title: "Verified partners", copy: "Trade with KYC-verified merchants who publish real completion rates and history." },
];

function WhyChoose() {
  return (
    <section className="border-y border-border py-14 sm:py-20" data-testid="why-choose">
      <div className="mx-auto max-w-7xl px-4">
        <div className="max-w-2xl">
          <h2 className="font-display text-2xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
            Why choose <Accent>PaisaCX</Accent>
          </h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground sm:text-base sm:leading-7">
            Security and performance built for Indian on-ramp and off-ramp trading — P2P and OTC in one place.
          </p>
        </div>
        <div className="mt-8 grid gap-4 sm:mt-12 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_CHOOSE.map(({ icon: Icon, title, copy }) => (
            <div
              key={title}
              tabIndex={0}
              className="interactive-tile panel-gradient cursor-default rounded-xl border border-l-2 border-border border-l-primary px-5 py-5 sm:px-6"
            >
              <div className="tile-icon flex h-10 w-10 items-center justify-center rounded-lg text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-4 text-base font-semibold sm:text-lg">{title}</h3>
              <p className="mt-2 text-sm leading-6 text-muted-foreground">{copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const HOW_STEPS = [
  { icon: Users, title: "Create an account", copy: "Complete a streamlined KYC flow in minutes with Aadhaar and PAN verification." },
  { icon: Banknote, title: "Add funds", copy: "Deposit INR with UPI, IMPS or NEFT — zero deposit fees on supported rails." },
  { icon: ArrowLeftRight, title: "Buy or sell crypto", copy: "Match with verified merchants on P2P or request a firm OTC quote for size." },
  { icon: Building2, title: "Withdraw funds", copy: "Cash out to your bank account in minutes once the trade settles." },
];

function HowItWorks() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:py-20">
      <SectionTitle>
        How <Accent>PaisaCX</Accent> works
      </SectionTitle>
      <p className="mt-3 max-w-xl text-sm text-muted-foreground sm:text-base">Get started in a few simple steps.</p>

      <ol className="mt-8 grid gap-4 sm:mt-10 sm:grid-cols-2 lg:grid-cols-4">
        {HOW_STEPS.map(({ icon: Icon, title, copy }, i) => (
          <li key={title} tabIndex={0} className="interactive-tile panel-gradient relative rounded-xl border border-border p-5">
            <span className="num absolute right-4 top-4 text-2xl font-bold text-primary/25">{i + 1}</span>
            <div className="tile-icon flex h-10 w-10 items-center justify-center rounded-lg text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <h3 className="mt-4 font-semibold">{title}</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{copy}</p>
          </li>
        ))}
      </ol>

      <div className="mt-8 grid gap-4 lg:grid-cols-2">
        <div className="interactive-tile panel-gradient overflow-hidden rounded-2xl border border-border">
          <img src={ESCROW_IMG} alt="" loading="lazy" width={1280} height={720} className="h-44 w-full object-cover transition duration-500 hover:scale-[1.03] sm:h-52" />
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-2 text-primary">
              <span className="tile-icon flex h-9 w-9 items-center justify-center rounded-lg">
                <LockKeyhole className="h-5 w-5" />
              </span>
              <h3 className="text-lg font-semibold text-foreground">Secure escrow system</h3>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              Every P2P trade is protected until both sides confirm. Funds stay locked until settlement or dispute review.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                Funds held in escrow until trade completion
              </li>
              <li className="flex gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                Dispute resolution available 24/7
              </li>
            </ul>
          </div>
        </div>

        <div className="interactive-tile panel-gradient overflow-hidden rounded-2xl border border-border">
          <img src={OTC_IMG} alt="" loading="lazy" width={1280} height={720} className="h-44 w-full object-cover transition duration-500 hover:scale-[1.03] sm:h-52" />
          <div className="p-5 sm:p-6">
            <div className="flex items-center gap-2 text-primary">
              <span className="tile-icon flex h-9 w-9 items-center justify-center rounded-lg">
                <Wand2 className="h-5 w-5" />
              </span>
              <h3 className="text-lg font-semibold text-foreground">Multiple payment options</h3>
            </div>
            <p className="mt-3 text-sm leading-6 text-muted-foreground">
              PaisaCX supports major Indian payment methods so you can buy and sell with the rails you already use.
            </p>
            <ul className="mt-4 space-y-2 text-sm text-muted-foreground">
              <li className="flex gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                UPI, IMPS, NEFT and bank transfers
              </li>
              <li className="flex gap-2">
                <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                Instant INR deposits and fast withdrawals
              </li>
            </ul>
            <Button asChild className="brand-gradient mt-5 h-11 px-5 shadow-brand">
              <Link to="/otc">
                Explore OTC desk <ArrowRight />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

function MarketRow({ a, spark }) {
  return (
    <tr className="interactive-row border-t border-border first:border-t-0 [&>td]:px-3">
      <td className="py-3.5">
        <div className="flex items-center gap-3">
          <AssetGlyph symbol={a.symbol} image={a.image} size={36} />
          <div>
            <p className="font-semibold">{a.symbol}</p>
            <p className="text-xs text-muted-foreground">{a.name}</p>
          </div>
        </div>
      </td>
      <td className="text-right">
        <PriceCell value={a.price} />
      </td>
      <td className="text-right">
        <ChangePill value={a.change_pct_24h} />
      </td>
      <td className="hidden text-right md:table-cell">
        <span className="inline-flex justify-end">
          <Sparkline data={spark} width={90} height={26} up={a.change_pct_24h >= 0} />
        </span>
      </td>
      <td className="num hidden text-right text-muted-foreground lg:table-cell">${fmtCompact(a.quote_volume_24h ?? a.volume_24h)}</td>
      <td className="hidden text-right sm:table-cell">
        <Button asChild size="sm" variant="outline" className="border-primary/40">
          <Link to="/p2p">P2P</Link>
        </Button>
      </td>
    </tr>
  );
}

function MarketTrend() {
  const [tab, setTab] = useState("core");
  const { rows: all, newest } = useLiveMarkets("USDT");

  const rows = useMemo(() => {
    if (tab === "gainers") {
      return all.filter((r) => r.change_pct_24h > 0).sort((a, b) => b.change_pct_24h - a.change_pct_24h).slice(0, 6);
    }
    if (tab === "new") {
      const bySym = new Map(all.map((r) => [r.symbol, r]));
      return newest.map((s) => bySym.get(s)).filter(Boolean).slice(0, 6);
    }
    return all.slice(0, 6);
  }, [all, newest, tab]);
  const sparkOf = useSparklines(useMemo(() => rows.map((r) => r.symbol), [rows]));

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:py-20" data-testid="market-trend">
      <SectionTitle action={<MoreButton to="/markets" label="See more" />}>
        Crypto <Accent>market trend</Accent>
      </SectionTitle>
      <div className="panel-gradient mt-8 rounded-xl border border-border p-4 shadow-card transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-card-hover sm:mt-10 sm:p-5">
        <div className="flex w-fit flex-wrap gap-1 rounded-lg border border-border bg-surface-2 p-1">
          {[
            ["core", "Core assets"],
            ["gainers", "24H gainers"],
            ["new", "New listings"],
          ].map(([k, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold transition duration-200 sm:text-sm ${
                tab === k ? "brand-gradient text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-surface hover:text-foreground"
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-background/40">
          <table className="w-full text-sm">
            <thead>
              <tr className="table-head [&>th]:px-3 [&>th]:py-2.5">
                <th className="text-left">Market</th>
                <th className="text-right">Last price</th>
                <th className="text-right">24h</th>
                <th className="hidden text-right md:table-cell">Chart</th>
                <th className="hidden text-right lg:table-cell">Volume</th>
                <th className="hidden text-right sm:table-cell">Buy</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0
                ? [0, 1, 2, 3].map((i) => (
                    <tr key={i}>
                      <td colSpan={6} className="px-3 py-2">
                        <div className="skeleton h-9 w-full" />
                      </td>
                    </tr>
                  ))
                : rows.map((a) => <MarketRow key={a.pair} a={a} spark={sparkOf(a.symbol)} />)}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

const P2P_STEPS = [
  [Users, "Pick a verified merchant", "Filter by price, limits and payment method. Every merchant shows real order history."],
  [LockKeyhole, "Crypto locks in escrow", "The seller's coins are held by PaisaCX the moment your order opens."],
  [BadgeCheck, "Pay and receive", "Pay over UPI, IMPS or bank transfer. Escrow releases once payment is confirmed."],
];

function P2PShowcase() {
  const { user } = useAuth();
  const [side, setSide] = useState("buy");
  const { p2pStats: stats, desks } = useLiveMarkets("USDT");
  const p2pPaused = desks?.p2p?.USDT === false;
  const inr = (n) => Math.round(n).toLocaleString("en-IN");
  const offers = p2pPaused
    ? []
    : (stats?.offers?.[side] || []).map((o) => ({
        ...o,
        completion: Math.round(Number(o.completion) || 0),
        limit: `${inr(o.min_inr)} – ${inr(o.max_inr)}`,
      }));

  return (
    <section className="mx-auto max-w-7xl px-4 py-14 sm:py-16">
      <div className="panel-gradient relative grid items-start gap-10 overflow-hidden rounded-3xl border border-primary/30 p-6 shadow-card transition-[border-color,box-shadow] duration-300 hover:border-primary/[0.45] hover:shadow-card-hover sm:p-12 lg:grid-cols-[1fr_1.15fr]">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <ArrowLeftRight className="h-3.5 w-3.5" /> Peer-to-peer marketplace
          </span>
          <h2 className="mt-4 text-2xl font-semibold leading-tight sm:text-5xl">
            Buy &amp; sell crypto <Accent>directly with people</Accent>
          </h2>
          <p className="mt-4 max-w-lg text-sm leading-6 text-muted-foreground sm:mt-5 sm:text-base sm:leading-7">
            Set your own price or take the best offer. Local payment methods and escrow on every order.
          </p>
          <ol className="mt-8 space-y-5">
            {P2P_STEPS.map(([Icon, t, c], i) => (
              <li key={t} className="flex gap-4">
                <div className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                  <Icon className="h-5 w-5" />
                  <span className="num absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
                    {i + 1}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold">{t}</h3>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">{c}</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button asChild className="brand-gradient h-11 px-6 shadow-brand">
              <Link to="/p2p">
                Explore P2P offers <ArrowRight />
              </Link>
            </Button>
            <Button asChild variant="outline" className="h-11 border-primary/40 px-6">
              <Link to={user ? "/p2p/my-ads?action=create" : "/register"}>Post an offer</Link>
            </Button>
          </div>
        </div>

        <div className="panel-gradient rounded-2xl border border-border shadow-panel backdrop-blur">
          <div className="flex items-center justify-between gap-3 border-b border-border p-4">
            <div className="flex rounded-lg bg-surface-2 p-1">
              {["buy", "sell"].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSide(s)}
                  className={`rounded-md px-4 py-1.5 text-sm font-semibold capitalize transition duration-200 ${
                    side === s
                      ? s === "buy"
                        ? "bg-up text-primary-foreground shadow-sm"
                        : "bg-down text-white shadow-sm"
                      : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
                  }`}
                >
                  {s} USDT
                </button>
              ))}
            </div>
            <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="h-2 w-2 animate-pulse rounded-full bg-up" /> Live · INR
            </span>
          </div>
          <div className="divide-y divide-border">
            {offers.length === 0 && (
              <p className="px-4 py-12 text-center text-sm text-muted-foreground" data-testid="p2p-showcase-empty">
                {p2pPaused ? "P2P trading is paused right now." : `No live ${side === "buy" ? "sell" : "buy"} offers right now. Post the first one.`}
              </p>
            )}
            {offers.map((o, i) => (
              <div key={`${o.merchant}-${i}`} className="interactive-row grid grid-cols-[1fr_auto] items-center gap-3 p-4 sm:grid-cols-[1.3fr_1fr_auto]">
                <div className="flex items-center gap-3">
                  <span className="brand-gradient flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-primary-foreground">
                    {o.merchant.slice(0, 2)}
                  </span>
                  <div className="min-w-0">
                    <p className="flex items-center gap-1 text-sm font-semibold">
                      {o.merchant}
                      <BadgeCheck className="h-3.5 w-3.5 text-primary" />
                      {i === 0 && <span className="ml-1 rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">Best</span>}
                    </p>
                    <p className="num text-xs text-muted-foreground">
                      {o.orders.toLocaleString("en-IN")} orders · {o.completion}%
                    </p>
                  </div>
                </div>
                <div className="hidden sm:block">
                  <p className="num text-lg font-semibold">₹{o.price.toFixed(2)}</p>
                  <p className="num text-xs text-muted-foreground">₹{o.limit}</p>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {o.methods.map((m) => (
                      <span key={m} className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="text-right">
                  <p className="num text-base font-semibold sm:hidden">₹{o.price.toFixed(2)}</p>
                  <Button
                    asChild
                    size="sm"
                    className={side === "buy" ? "bg-up text-primary-foreground hover:bg-up/90" : "bg-down text-white hover:bg-down/90"}
                  >
                    <Link to="/p2p">{side === "buy" ? "Buy" : "Sell"}</Link>
                  </Button>
                </div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 border-t border-border text-center">
            {[
              ["₹0", "Taker fees"],
              [stats?.avg_release_min == null ? "—" : `${stats.avg_release_min} min`, "Avg. release"],
              ["100%", "Escrow-backed"],
            ].map(([v, l]) => (
              <div key={l} className="border-r border-border p-3 last:border-r-0">
                <p className="num text-sm font-semibold text-primary sm:text-base">{v}</p>
                <p className="text-[11px] text-muted-foreground">{l}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const TESTIMONIALS = [
  {
    quote: "PaisaCX made USDT trading simple. Escrow and fast UPI releases mean I no longer juggle three apps for one trade.",
    name: "Rajesh Kumar",
    role: "Active trader",
    city: "Delhi",
    initials: "RK",
  },
  {
    quote: "As someone new to crypto, the guided P2P flow and 24/7 support got me from KYC to my first buy without stress.",
    name: "Priya Sharma",
    role: "Business owner",
    city: "Mumbai",
    initials: "PS",
  },
  {
    quote: "Best rates and the fastest INR withdrawals I've used. Session controls and 2FA give me real peace of mind.",
    name: "Amitabh Singh",
    role: "Investment advisor",
    city: "Bangalore",
    initials: "AS",
  },
  {
    quote: "For block size I move to the OTC desk. Firm quotes, clear settlement — and P2P still covers my day-to-day USDT.",
    name: "Sneha Patel",
    role: "Tech entrepreneur",
    city: "Ahmedabad",
    initials: "SP",
  },
];

function Testimonials() {
  const [active, setActive] = useState(0);
  const t = TESTIMONIALS[active];
  const prev = () => setActive((i) => (i === 0 ? TESTIMONIALS.length - 1 : i - 1));
  const next = () => setActive((i) => (i === TESTIMONIALS.length - 1 ? 0 : i + 1));
  const navBtn =
    "flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-surface text-foreground transition duration-200 hover:border-primary hover:bg-primary/15 hover:text-primary active:scale-95";

  return (
    <section className="border-y border-border py-14 sm:py-20">
      <div className="mx-auto max-w-7xl px-4">
        <div className="grid items-end gap-6 lg:grid-cols-[1fr_auto]">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-primary">Community</p>
            <h2 className="mt-2 font-display text-2xl font-semibold leading-tight sm:text-4xl lg:text-5xl">
              Real voices, <Accent>real experiences</Accent>
            </h2>
            <p className="mt-3 text-sm text-muted-foreground sm:text-base">Hear from traders across India who use PaisaCX daily.</p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Previous testimonial" onClick={prev} className={navBtn}>
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button type="button" aria-label="Next testimonial" onClick={next} className={navBtn}>
              <ArrowRight className="h-4 w-4" />
            </button>
            <span className="num ml-2 text-sm text-muted-foreground">
              {active + 1} / {TESTIMONIALS.length}
            </span>
          </div>
        </div>

        <div className="mt-8 grid gap-4 sm:mt-10 lg:grid-cols-[1.4fr_1fr]">
          <blockquote className="panel-gradient relative overflow-hidden rounded-2xl border border-border p-6 sm:p-8 lg:p-10">
            <Quote className="absolute -right-2 -top-2 h-24 w-24 text-primary/10 sm:h-32 sm:w-32" aria-hidden />
            <div className="relative">
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary">
                <BadgeCheck className="h-3.5 w-3.5" /> Verified trader
              </span>
              <p key={t.name} className="mt-5 font-display text-xl font-medium leading-snug tracking-tight text-foreground sm:text-2xl lg:text-[1.75rem] lg:leading-[1.35]">
                “{t.quote}”
              </p>
              <footer className="mt-8 flex items-center gap-4 border-t border-border pt-6">
                <span className="brand-gradient flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-sm font-bold text-primary-foreground">
                  {t.initials}
                </span>
                <div>
                  <p className="font-semibold">{t.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {t.role} · {t.city}
                  </p>
                </div>
              </footer>
            </div>
          </blockquote>

          <div className="flex flex-col gap-2">
            {TESTIMONIALS.map((person, i) => {
              const selected = i === active;
              return (
                <button
                  key={person.name}
                  type="button"
                  onClick={() => setActive(i)}
                  className={`group flex w-full items-center gap-3 rounded-xl border px-4 py-3.5 text-left transition duration-200 active:scale-[0.99] ${
                    selected
                      ? "border-primary/[0.55] bg-primary/[0.12] shadow-card"
                      : "border-border bg-surface/60 hover:border-primary/[0.45] hover:bg-primary/10 hover:shadow-card"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-xs font-bold transition duration-200 ${
                      selected ? "brand-gradient text-primary-foreground" : "bg-surface-2 text-muted-foreground group-hover:bg-primary/20 group-hover:text-primary"
                    }`}
                  >
                    {person.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className={`truncate text-sm font-semibold ${selected ? "text-foreground" : "text-foreground/90"}`}>{person.name}</p>
                    <p className="truncate text-xs text-muted-foreground">
                      {person.role} · {person.city}
                    </p>
                  </div>
                  {selected ? (
                    <span className="hidden h-2 w-2 shrink-0 rounded-full bg-primary sm:block" />
                  ) : (
                    <ArrowRight className="hidden h-3.5 w-3.5 shrink-0 text-muted-foreground/50 transition group-hover:translate-x-0.5 group-hover:text-primary sm:block" />
                  )}
                </button>
              );
            })}

            <div className="mt-2 grid grid-cols-3 gap-2 border-t border-border pt-4">
              {[
                ["200k+", "Traders"],
                ["24/7", "Support"],
                ["4.9", "Rating"],
              ].map(([v, l]) => (
                <div key={l} className="text-center">
                  <p className="num text-base font-semibold text-primary sm:text-lg">{v}</p>
                  <p className="text-[11px] text-muted-foreground">{l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  const fx = useUsdtInr();
  return (
    <SiteFrame>
      <div className="page-fade" data-testid="landing-page">
        <Hero />
        <div className="section-band relative z-10 overflow-x-clip">
          <LiveInsights fx={fx} />
          <WhyChoose />
          <HowItWorks />
          <MarketTrend />
          <P2PShowcase fx={fx} />
          <Testimonials />
        </div>
      </div>
    </SiteFrame>
  );
}
